import { supabase } from '../../../core/lib/supabase';
import { TemplateBlock, BlockInsertionRequest, BlockInsertionResult } from '../types/blockTypes';
import { getBackendAuthHeaders } from '../../../shared/services/backendAuthService';

const ORGANIZATION_BACKEND_BASE_URL = process.env.VITE_ORGANIZATION_BACKEND_URL || '';

function buildUrl(baseUrl: string, path: string): string {
  const normalizedBase = baseUrl.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

// System-level IDs for blank/empty projects (bypass RLS)
const BLANK_WORKPILL_ID = '00000000-0000-0000-0000-000000000000';
const BLANK_TEMPLATE_ID = '00000000-0000-0000-0000-000000000000';

export class BlocksService {
  
  /**
   * Obtiene todos los bloques de una template
   */
  static async getTemplateBlocks(templateId: string): Promise<TemplateBlock[]> {
    try {
      const { data, error } = await supabase
        .from('template_blocks')
        .select('*')
        .eq('template_id', templateId)
        .order('default_order', { ascending: true, nullsFirst: false });

      if (error) throw error;
      
      // 🔍 LOG DIAGNÓSTICO
      console.log('🔍 [BLOCKS DEBUG] Template ID:', templateId);
      console.log('🔍 [BLOCKS DEBUG] Blocks fetched from DB:', data?.length || 0);
      console.log('🔍 [BLOCKS DEBUG] Block details:', data);
      
      return data || [];
    } catch (error) {
      console.error('Error fetching template blocks:', error);
      return [];
    }
  }

  /**
   * Obtiene todos los bloques sin filtrar por template_id
   */
  static async getAllBlocks(): Promise<TemplateBlock[]> {
    try {
      const { data, error } = await supabase
        .from('template_blocks')
        .select('*')
        .order('default_order', { ascending: true, nullsFirst: false });

      if (error) throw error;
      
      // 🔍 LOG DIAGNÓSTICO
      console.log('🔍 [BLOCKS DEBUG] Fetching all blocks without filtering');
      console.log('🔍 [BLOCKS DEBUG] Blocks fetched from DB:', data?.length || 0);
      console.log('🔍 [BLOCKS DEBUG] Block details:', data);
      
      return data || [];
    } catch (error) {
      console.error('Error fetching all blocks:', error);
      return [];
    }
  }

  /**
   * Llama al endpoint de warm-up de organización
   */
  static async warmUpOrganization(): Promise<void> {
    try {
      const orgWarmUpUrl = ORGANIZATION_BACKEND_BASE_URL
        ? buildUrl(ORGANIZATION_BACKEND_BASE_URL, '/warm-up/')
        : '';
      
      if (!orgWarmUpUrl) {
        console.warn('⚠️ VITE_ORGANIZATION_BACKEND_URL not configured');
        return;
      }
      
      console.log('🔥 Calling organization warm-up endpoint...');

      const headers = await getBackendAuthHeaders(true);
      const response = await fetch(orgWarmUpUrl, {
        method: 'GET',
        headers,
      });
      
      if (response.ok) {
        const data = await response.json().catch(() => ({ message: 'Warm up reached' }));
        console.log('✅ Warm-up completed:', data);
      } else {
        console.warn('⚠️ Warm-up failed with status:', response.status);
      }
    } catch (error) {
      // No lanzar error, solo loggear ya que no es crítico para el usuario
      console.warn('⚠️ Warm-up error (non-critical):', error);
    }
  }

  /**
   * Llama al organization backend para insertar bloques
   */
  static async insertBlocks(
    templateId: string,
    workpillId: string,
    executionId: string,
    blockOrders: BlockInsertionRequest[]
  ): Promise<BlockInsertionResult> {
    try {
      const orgBackendUrl = ORGANIZATION_BACKEND_BASE_URL;
      
      if (!orgBackendUrl) {
        throw new Error('Missing VITE_ORGANIZATION_BACKEND_URL environment variable');
      }
      
      const headers = await getBackendAuthHeaders(true);
      const response = await fetch(
        buildUrl(orgBackendUrl, `/templates/${templateId}/process`),
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            workpill_id: workpillId,
            execution_id: executionId,
            from_latest: true,
            block_order: blockOrders
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        ...result
      };
    } catch (error) {
      console.error('Error inserting blocks:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Obtiene el template_id asociado a un workpill
   */
  static async getTemplateIdFromWorkpill(workpillId: string): Promise<string | null> {
    // Blank workpill: return hardcoded template ID (bypasses RLS)
    if (!workpillId || workpillId === BLANK_WORKPILL_ID) {
      console.log('🔍 [BLOCKS SERVICE] Blank / no workpill — using blank catalog template id:', BLANK_TEMPLATE_ID);
      return BLANK_TEMPLATE_ID;
    }

    try {
      const { data, error } = await supabase
        .from('workpill_templates')
        .select('template_id')
        .eq('workpill_id', workpillId)
        .limit(1)
        .single();

      if (error) throw error;
      return data?.template_id || null;
    } catch (error) {
      console.error('Error fetching template ID:', error);
      return null;
    }
  }

  /**
   * Descarga un documento desde Supabase Storage y lo carga en Word
   */
  static async loadDocumentFromStorage(path: string, bucket: string = 'execution-templates'): Promise<void> {
    try {
      console.log(`📥 Downloading updated document from bucket "${bucket}":`, path);
      
      const { data: blob, error: downloadError } = await supabase.storage
        .from(bucket)
        .download(path);
      
      if (downloadError) {
        throw downloadError;
      }
      
      if (!blob || blob.size === 0) {
        throw new Error('Downloaded document is empty');
      }
      
      console.log('✅ Document downloaded:', blob.size, 'bytes');
      console.log('📂 Loading in Word...');
      
      // Importar WordService dinámicamente para evitar dependencias circulares
      const { WordService } = await import('../../../shared/services/wordService');
      await WordService.loadDocumentFromBlob(blob);
      
      console.log('✅ Updated document loaded successfully in Word');
    } catch (error) {
      console.error('❌ Error loading document from Storage:', error);
      throw error;
    }
  }

  /**
   * Genera una signed URL para previsualizar un bloque
   * Maneja tanto URLs completas como paths relativos guardados en block.content
   */
  static async getBlockPreviewUrl(block: TemplateBlock): Promise<string | null> {
    try {
      // Verificar si el content existe
      if (!block.content) {
        console.log('⚠️ Block has no content');
        return null;
      }

      // Detectar si es un archivo .docx por extensión o por content_format
      const isDocx = block.content.toLowerCase().endsWith('.docx') || 
                     block.content_format === 'docx' || 
                     block.content_format === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      if (!isDocx) {
        console.log('⚠️ Block has no previewable content (not .docx)');
        console.log(`   - content: ${block.content}`);
        console.log(`   - content_format: ${block.content_format}`);
        return null;
      }

      console.log(`📄 Generating preview URL for block: ${block.title}`);
      console.log(`📄 Original content: ${block.content}`);
      
      // Extraer el path relativo del bucket
      let path: string;
      
      // Si el content es una URL completa de Supabase Storage, extraer el path relativo
      if (block.content.startsWith('http://') || block.content.startsWith('https://')) {
        try {
          const url = new URL(block.content);
          
          // Patrón para URLs de Supabase Storage:
          // https://[project].supabase.co/storage/v1/object/public/[bucket]/[path]
          // Necesitamos extraer solo [path]
          
          // Buscar el patrón /blocks/ en la URL y tomar todo lo que viene después
          const pathMatch = url.pathname.match(/\/blocks\/(.+)$/);
          
          if (pathMatch && pathMatch[1]) {
            // Extraer el path relativo después de /blocks/
            path = decodeURIComponent(pathMatch[1]);
            console.log(`📄 Relative path extracted from URL: ${path}`);
          } else {
            // Si no encontramos el patrón /blocks/, intentar extraer desde /public/blocks/
            const publicMatch = url.pathname.match(/\/public\/blocks\/(.+)$/);
            if (publicMatch && publicMatch[1]) {
              path = decodeURIComponent(publicMatch[1]);
              console.log(`📄 Relative path extracted from URL (public): ${path}`);
            } else {
              // Fallback: intentar usar los últimos segmentos de la URL
              const segments = url.pathname.split('/').filter(s => s);
              const blocksIndex = segments.indexOf('blocks');
              if (blocksIndex !== -1 && segments.length > blocksIndex + 1) {
                path = segments.slice(blocksIndex + 1).join('/');
                console.log(`📄 Relative path extracted (fallback): ${path}`);
              } else {
                // Último recurso: usar block.id/document.docx
                console.warn('⚠️ Could not extract path from URL, using block.id as fallback');
                path = `${block.id}/document.docx`;
              }
            }
          }
        } catch (urlError) {
          console.warn('⚠️ Error parsing URL, using fallback:', urlError);
          // Si falla el parseo, intentar construir el path desde block.id
          path = `${block.id}/document.docx`;
        }
      } else if (block.content.includes('/')) {
        // Si es un path relativo que incluye '/', usar directamente
        // Pero verificar que no empiece con /blocks/ o /public/blocks/
        path = block.content.replace(/^\/?(public\/)?blocks\//, '');
        console.log(`📄 Relative path (without full URL): ${path}`);
      } else {
        // Si no tiene '/', construir el path con block.id
        path = `${block.id}/document.docx`;
        console.log(`📄 Path built from block.id: ${path}`);
      }
      
      console.log(`📄 Final path for createSignedUrl: ${path}`);
      
      // Generar signed URL desde el bucket de blocks
      const { data, error } = await supabase.storage
        .from('blocks')
        .createSignedUrl(path, 3600); // 1 hora de expiración

      if (error) {
        console.error('❌ Error generating preview URL:', error);
        console.error('   - Path used:', path);
        console.error('   - Error details:', error);
        return null;
      }
      
      console.log('✅ Preview URL generated correctly');
      return data?.signedUrl || null;
    } catch (error) {
      console.error('❌ Error generating preview URL:', error);
      return null;
    }
  }
}

